import { EyeIcon, EyeOffIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import type { DataError } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import styles from './form.module.css';

/**
 * The form plumbing both user forms need.
 *
 * There are two of them and there always will be, because the API has two shapes: an
 * administrator editing anybody through `UserEditCard`, and a person editing
 * themselves through `UserProfileCard` — which has no `role`, no `isActive` and no
 * `username`, so a privilege cannot be raised through it. Sharing the *form* between
 * them would undo that; sharing the parts a form is built from does not.
 *
 * In `features/users/` rather than `components/ui/` because only this feature uses
 * them. The moment a second feature does, they belong there instead.
 */

/** A value that cannot be edited, shown in the shape of a field so a form reads evenly. */
export const Readout = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    <span className={cx(styles.readout, mono && styles.readoutMono)}>{value}</span>
  </div>
);

/** The eye inside a password field. */
export const PasswordReveal = ({ shown, onToggle }: { shown: boolean; onToggle: () => void }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={styles.reveal}
      onClick={onToggle}
      aria-label={t(shown ? 'auth.hidePassword' : 'auth.showPassword')}
      title={t(shown ? 'auth.hidePassword' : 'auth.showPassword')}
      // Not a Tab stop: it reveals what was just typed and sits between the password
      // field and the next one.
      tabIndex={-1}
    >
      {shown ? <EyeOffIcon className={styles.revealIcon} /> : <EyeIcon className={styles.revealIcon} />}
    </button>
  );
};

/**
 * Spreads a failed write over the inputs that caused it.
 *
 * The server's `path` may be dotted (`card.name`), so only the last segment is
 * matched against `fieldOf`. Anything that matches nothing comes back as `loose` — an
 * error with no field to attach it to is still an error, and dropping it is what
 * silently ignoring an unrecognised path amounts to.
 */
export const attachErrors = <K extends string>(
  errors: readonly DataError[],
  fieldOf: Readonly<Record<string, K>>,
): { attached: Partial<Record<K, string>>; loose: string[] } => {
  const attached: Partial<Record<K, string>> = {};
  const loose: string[] = [];

  for (const error of errors) {
    const segment = error.path?.split('.').pop() ?? '';
    const field = fieldOf[segment];
    const detail = error.message ?? error.errorCode ?? '';

    if (detail === '') continue;
    if (field) attached[field] = detail;
    else loose.push(detail);
  }

  return { attached, loose };
};
